import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { OverlayPanel } from "primereact/overlaypanel";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import apiCalls from "../../../../config/apiCalls";
import fetchMethodRequest from "../../../../config/service";
import showToasterMessage from "../../../UI/ToasterMessage/toasterMessage";
import DoInputField from "../../../Form/Fields/DoInputField";
import DoTextareaField from "../../../Form/Fields/DoTextareaField";
import "./SideBySideLayout.css";

const Modules = ({ modules, setModules, fetchModules }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const moduleId = searchParams.get("module");

  const [showModuleForm, setShowModuleForm] = useState(false);
  const [selectedModule, setSelectedModule] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const overlayRef = useRef(null);

  const {
    handleSubmit,
    reset,
    setValue,
    control,
    formState: { errors },
  } = useForm();

  useEffect(() => {
    if (modules?.length > 0 && !moduleId) {
      handleModuleClick(modules[0]);
    }
  }, [modules, moduleId]);

  // ===================
  // FORM FIELD CONFIG
  // ===================
  const moduleFormFields = [
    {
      name: 'name',
      type: 'text',
      placeholder: 'Module Name',
      label: 'Module Name',
      width: 80,
      id: 'name',
      variant: "outlined",
      value: "",
    },
    {
      name: 'description',
      type: 'textArea',
      placeholder: 'Description',
      label: 'Description',
      width: 80,
      id: 'description',
      onBlur: true,
      variant: "outlined",
      value: "",
    },
  ];

  // ===================
  // HANDLERS
  // ===================
  const handleModuleClick = (module) => {
    navigate(`?module=${module._id}`);
  };

  const handleCloseModuleForm = () => {
    setShowModuleForm(false);
    reset();
    moduleFormFields.map(i => {
      setValue(i.name, "")
    })
    setIsEditing(false);
    setSelectedModule(null);
  };

  // ===================
  // Create Module Function
  // ===================
  const handleModuleCreation = async (data) => {
    let method = "POST"
    let path = apiCalls.modules
    let message = "created"

    if (isEditing && selectedModule) {
      const updatedData = {
        _id: selectedModule._id,
        name: data.name,
        description: data.description,
      };
      method = "PUT",
        path = apiCalls.modules + `/${selectedModule._id}`
      message = "updated"
    }

    try {
      const res = await fetchMethodRequest(method, path, {
        moduleName: data.name,
        description: data.description,
      });

      if (res?.testCasesModulesId) {
        showToasterMessage(`Module ${message} successfully`, "success");
        fetchModules?.();
        navigate(`/testCases?module=${res?.testCasesModulesId}`)
        handleCloseModuleForm();
      } else {
        showToasterMessage("Failed to create module", "error");
      }
    } catch (err) {
      console.error(err);
      showToasterMessage("Error creating module", "error");
    }
  };


  // ===================
  // Edit Function
  // ===================
  const handleEditModule = (module) => {
    setIsEditing(true);
    setSelectedModule(module);
    setShowModuleForm(true);

    // 🧩 Prefill form fields
    setValue("name", module.moduleName || "");
    setValue("description", module.description || "");
  };


  // ===================
  // Delete Function
  // ===================
  const handleDeleteModule = async (module) => {
    try {
      const url = apiCalls.modules + "/multiDelete";
      let body = {
        selectedIds: [module._id]
      }
      const res = await fetchMethodRequest("POST", url, body);

      if (res?.respCode === 206) {
        showToasterMessage("Module deleted successfully", "success");
        if (moduleId == module._id) {
          fetchModules?.();
          setTimeout(() => {
            navigate("/testCases")
          }, 100);
        } else {
          fetchModules?.();
        }
      } else {
        showToasterMessage("Failed to delete module", "error");
      }
    } catch (err) {
      console.error(err);
      showToasterMessage("Error deleting module", "error");
    }
  };

  const showOverlay = (event, module) => {
    setSelectedModule(module);
    overlayRef.current.toggle(event);
  };

  // ===================
  // MODULE FORM (Dialog)
  // ===================
  const handleModuleForm = () => (
    <div className="mt-4" style={{ fontSize: "13px", padding: "10px" }}>
      <form onSubmit={handleSubmit(handleModuleCreation)}>
        {moduleFormFields.map((item, index) => (
          <div key={index} style={{ marginBottom: '15px' }}>
            <Controller
              key={index}
              name={item.name}
              control={control}
              render={({ field, fieldState }) =>
                item.type === "text" ? (
                  <DoInputField
                    input={field}
                    id={field.id}
                    name={field.name}
                    label={item.label}
                    variant={field.variant}
                    field={field}
                    type="text"
                    fieldState={fieldState}
                    errors={errors}
                    style={item.style}
                    placeholder={item.placeholder}
                  />
                ) : (
                  <DoTextareaField
                    input={field}
                    id={field.id}
                    name={field.name}
                    label={item.label}
                    variant={field.variant}
                    field={field}
                    type="text"
                    fieldState={fieldState}
                    errors={errors}
                    style={item.style}
                    placeholder={item.placeholder}
                  />
                )
              }
            />
          </div>
        ))}

        <Button color="primary" type="submit" style={{ marginTop: "20px" }}>
          {isEditing ? "Update Module" : "Create Module"}
        </Button>
      </form>
    </div>
  );

  // ===================
  // JSX RETURN
  // ===================
  return (
    <>
      <div className="col-md-3 col-lg-3 side-folder-list">
        <div
          className="d-flex justify-content-between align-items-center mb-3 p-3"
          style={{ borderBottom: "3px solid #00356c" }}
        >
          <h3 style={{ color: "#12225f", fontWeight: "bold" }}>Modules</h3>
          <Button raised size="small" onClick={() => setShowModuleForm(true)}>
            <FontAwesomeIcon icon="folder-plus" className="pl-1" size="lg" />
          </Button>
        </div>

        <ul className="folder-list">
          {modules.map((module) => (
            <li
              key={module._id}
              className={`folder-item-testcase ${moduleId === module._id ? "active" : ""
                }`}
              onClick={() => handleModuleClick(module)}
            >
              <div className="folder-name">
                <span>{module.moduleName}</span>
                <span className="count">({module.count})</span>
              </div>

              {/* ⚙️ Overlay menu */}
              <div
                className="folder-actions"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  className="options-button"
                  onClick={(e) => showOverlay(e, module)}
                >
                  <i className="pi pi-ellipsis-v"></i>
                </button>
              </div>
            </li>
          ))}
        </ul>
      </div>

      {/* ✅ Single reusable OverlayPanel */}
      <OverlayPanel ref={overlayRef} className="custom-overlay" dismissable>
        {selectedModule && (
          <ul className="overlay-list">
            <li
              className="overlay-list-item"
              onClick={() => {
                handleEditModule(selectedModule);
                overlayRef.current.hide();
              }}
            >
              <FontAwesomeIcon icon="edit" className="me-2" color="green" />
              Edit
            </li>
            <li
              className="overlay-list-item"
              onClick={() => {
                handleDeleteModule(selectedModule);
                overlayRef.current.hide();
              }}
            >
              <i className="pi pi-trash me-2"></i> Delete
            </li>
          </ul>
        )}
      </OverlayPanel>

      <Dialog
        header={isEditing ? "Edit Module" : "Add New Module"}
        visible={showModuleForm}
        style={{ width: "30vw" }}
        modal
        draggable={false}
        onHide={handleCloseModuleForm}
      >
        {handleModuleForm()}
      </Dialog>
    </>
  );
};

export default Modules;
